import React from "react";
import syFlagAsset from "../assets/flags/sy.svg";
import "./countryFlags.css";

/** ISO2 codes that use a bundled image instead of regional-indicator emoji. */
const CUSTOM_FLAG_ASSETS = {
  SY: syFlagAsset,
};

const FALLBACK_EMOJI = "🌍";

/**
 * @param {unknown} input
 * @returns {string} Uppercase ISO 3166-1 alpha-2 or ""
 */
export function normalizeCountryCode(input) {
  const raw = (input ?? "").toString().trim();
  if (!raw) return "";

  const upper = raw.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;

  const lower = raw.toLowerCase();
  if (/^[a-z]{2}$/.test(lower)) return lower.toUpperCase();

  const token = upper.split(/[\s,|/_-]+/).find((part) => /^[A-Z]{2}$/.test(part));
  return token || "";
}

function iso2ToFlagEmoji(iso2) {
  try {
    const code = normalizeCountryCode(iso2);
    if (!code) return "";
    return String.fromCodePoint(
      0x1f1e6 + code.charCodeAt(0) - 65,
      0x1f1e6 + code.charCodeAt(1) - 65
    );
  } catch {
    return "";
  }
}

/**
 * @param {unknown} countryCode — e.g. SY, sy, DE
 * @returns {string} Emoji flag or custom asset URL (Syria)
 */
export function getCountryFlag(countryCode) {
  const code = normalizeCountryCode(countryCode);
  if (!code) return FALLBACK_EMOJI;

  if (isCustomCountryFlag(code)) {
    return CUSTOM_FLAG_ASSETS[code];
  }

  return iso2ToFlagEmoji(code) || FALLBACK_EMOJI;
}

/**
 * @param {unknown} countryCode
 * @returns {boolean}
 */
export function isCustomCountryFlag(countryCode) {
  const code = normalizeCountryCode(countryCode);
  return Boolean(code && CUSTOM_FLAG_ASSETS[code]);
}

/**
 * Single render path for country flags (emoji or custom SVG asset).
 *
 * @param {{ countryCode?: unknown, className?: string, imgClassName?: string }} props
 */
export function CountryFlagGlyph({
  countryCode,
  className = "",
  imgClassName = "country-flag-glyph__img",
}) {
  const code = normalizeCountryCode(countryCode);

  if (isCustomCountryFlag(code)) {
    return (
      <img
        src={getCountryFlag(code)}
        alt=""
        className={imgClassName || className}
        width={20}
        height={14}
        loading="lazy"
        decoding="async"
        aria-hidden
      />
    );
  }

  return (
    <span className={className} aria-hidden>
      {getCountryFlag(code)}
    </span>
  );
}
